/* ==========================================================================
   Newsletter popup — scratch-card offer

   Shows once per visitor per `data-frequency` days. Closing it leaves a small
   chip that invites them back; signing up silences both for a year. The card
   itself is a canvas painted with the ice photo, erased under the pointer
   with destination-out compositing; once enough of it is gone the frost melts
   away and the prize is revealed, then the email step slides in.
   ========================================================================== */

window.LUVZ = window.LUVZ || {};

LUVZ.newsletterPopup = (function () {
  'use strict';

  var KEY_DISMISSED = 'luvz_popup_dismissed';
  var KEY_SIGNED_UP = 'luvz_popup_signedup';
  var KEY_CHIP = 'luvz_chip_dismissed';

  var popup, chip, frequency, delay;
  var scratch = { inited: false, revealed: false, strokes: 0 };

  /* ---------- storage ----------------------------------------------------- */

  function get(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function set(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* no-op */ }
  }
  function until(key) {
    var v = get(key);
    return !!(v && Date.now() < parseInt(v, 10));
  }
  function suppress(key, days) {
    set(key, String(Date.now() + days * 864e5));
  }

  var signedUp = function () { return !!get(KEY_SIGNED_UP); };

  /* ---------- open / close ------------------------------------------------ */

  function showChip() {
    if (chip && !signedUp() && !until(KEY_CHIP)) chip.hidden = false;
  }
  function hideChip() {
    if (chip) chip.hidden = true;
  }

  function open() {
    popup.classList.add('show');
    popup.setAttribute('aria-hidden', 'false');
    hideChip();
    var card = popup.querySelector('.popup__card');
    if (card) card.classList.add('in');
    /* The card animates in, so the canvas cannot be sized until it has
       settled. Try on the next frame and again when the transition ends;
       initScratch bails out until the zone has real dimensions. */
    requestAnimationFrame(function () {
      requestAnimationFrame(initScratch);
    });
  }

  function close() {
    popup.classList.remove('show');
    popup.setAttribute('aria-hidden', 'true');
    /* closing the success state must not shorten the post-signup pause */
    suppress(KEY_DISMISSED, signedUp() ? 365 : frequency);
    if (!signedUp()) showChip();
  }

  /* ---------- reveal sequence ---------------------------------------------
     The frost finishes melting, then the whole card dissolves and the prize
     is left standing alone before the claim step slides in. */

  function reveal() {
    if (scratch.revealed) return;
    scratch.revealed = true;

    var gameStep = popup.querySelector('[data-scratch-step="game"]');
    var claimStep = popup.querySelector('[data-scratch-step="claim"]');
    var won = popup.querySelector('[data-scratch-won]');
    var hint = gameStep && gameStep.querySelector('.scratch__hint');
    if (scratch.canvas) scratch.canvas.classList.add('is-done');

    function toClaim() {
      if (gameStep) gameStep.hidden = true;
      if (!claimStep) return;
      claimStep.classList.add('is-entering');
      claimStep.hidden = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { claimStep.classList.remove('is-entering'); });
      });
      var email = claimStep.querySelector('input[type=email]');
      if (email) {
        setTimeout(function () {
          try { email.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
        }, 550);
      }
    }

    setTimeout(function () {
      if (scratch.stage) scratch.stage.classList.add('is-melting');
      if (hint) hint.classList.add('is-melting');
      setTimeout(function () {
        if (won) {
          won.hidden = false;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { won.classList.add('in'); });
          });
        }
        setTimeout(function () {
          if (gameStep) gameStep.classList.add('is-leaving');
          setTimeout(toClaim, 450);
        }, 1900);
      }, 420);
    }, 500);
  }

  /* ---------- the scratch canvas ------------------------------------------ */

  function initScratch() {
    if (scratch.inited) return;
    var canvas = popup.querySelector('[data-scratch-canvas]');
    if (!canvas) return;

    var zone = canvas.parentElement;
    var rect = zone.getBoundingClientRect();
    /* not laid out yet — transitionend or the first pointer over it retries */
    if (rect.width < 10 || rect.height < 10) return;

    scratch.inited = true;
    scratch.canvas = canvas;
    scratch.stage = canvas.closest('[data-scratch-stage]') || zone;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    var w = rect.width;
    var h = rect.height;
    var initiallyClear = 0;

    function sample() {
      var step = 24;
      var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      var clear = 0;
      var total = 0;
      for (var y = 0; y < canvas.height; y += step) {
        for (var x = 0; x < canvas.width; x += step) {
          total++;
          if (data[(y * canvas.width + x) * 4 + 3] === 0) clear++;
        }
      }
      return { clear: clear, total: total };
    }

    function attach() {
      var drawing = false;
      var last = null;

      function point(e) {
        var b = canvas.getBoundingClientRect();
        return { x: e.clientX - b.left, y: e.clientY - b.top };
      }
      function erase(a, b) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = 48;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x + 0.1, b.y + 0.1);
        ctx.stroke();
      }
      function check() {
        if (scratch.revealed) return;
        try {
          var s = sample();
          var opaque = s.total - initiallyClear;
          if (opaque > 0 && (s.clear - initiallyClear) / opaque >= 0.42) reveal();
        } catch (err) {
          if (scratch.strokes > 140) reveal();   /* tainted canvas fallback */
        }
      }

      canvas.addEventListener('pointerdown', function (e) {
        drawing = true;
        last = point(e);
        erase(last, last);
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
        e.preventDefault();
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!drawing) return;
        var p = point(e);
        erase(last, p);
        last = p;
        scratch.strokes++;
        if (scratch.strokes % 6 === 0) check();
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        canvas.addEventListener(ev, function () { drawing = false; check(); });
      });
      canvas.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); reveal(); }
      });
    }

    function drawBrandCover() {
      ctx.fillStyle = '#14171c';
      ctx.fillRect(0, 0, w, h);
      var g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, 'rgba(46,82,217,.30)');
      g.addColorStop(1, 'rgba(125,232,168,.20)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(245,244,239,.95)';
      ctx.font = '600 ' + Math.round(Math.min(w * 0.24, 110)) + 'px Archivo, sans-serif';
      ctx.fillText('LUVZ', w / 2, h / 2);
      ctx.font = '600 11px Archivo, sans-serif';
      ctx.fillStyle = 'rgba(245,244,239,.55)';
      ctx.fillText('S C R A T C H   T O   R E V E A L', w / 2, h - 26);
      attach();
    }

    /* Paint the cover with the ice photo so the scratch layer is identical to
       what sits beneath it until the first stroke lands. */
    var src = canvas.getAttribute('data-cover-src');
    if (!src) { drawBrandCover(); return; }

    var img = new Image();
    img.onload = function () {
      ctx.drawImage(img, 0, 0, w, h);
      try { initiallyClear = sample().clear; } catch (e) { /* no-op */ }
      attach();
    };
    img.onerror = drawBrandCover;
    img.src = src;
  }

  /* ---------- wiring ------------------------------------------------------ */

  function init() {
    popup = document.querySelector('[data-popup]');
    if (!popup) return;
    chip = document.querySelector('[data-popup-chip]');
    frequency = parseInt(popup.getAttribute('data-frequency'), 10) || 7;
    delay = parseInt(popup.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 5;

    popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-popup-close]')) close();
    });

    /* retries that survive a slow or interrupted open animation */
    popup.addEventListener('transitionend', function (e) {
      if (e.target === popup || e.target.classList.contains('popup__card')) initScratch();
    });
    popup.addEventListener('pointerover', function (e) {
      if (e.target.closest('[data-scratch-stage]')) initScratch();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('show')) close();
    });

    if (chip) {
      chip.addEventListener('click', function (e) {
        if (e.target.closest('[data-popup-chip-close]')) {
          suppress(KEY_CHIP, frequency);
          hideChip();
          return;
        }
        if (e.target.closest('[data-popup-chip-open]')) open();
      });
    }

    /* The claim step posts to Shopify's customer form in the background, so
       the email lands in the Shopify customer list (tagged "newsletter",
       with marketing consent) and the success step still animates in. If
       the request fails the form falls back to a normal submit. */
    var form = popup.querySelector('form[data-newsletter-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.disabled = true;
        LUVZ.signup(form).then(function () {
          set(KEY_SIGNED_UP, '1');
          suppress(KEY_DISMISSED, 365);
          var code = popup.getAttribute('data-code');
          if (code) fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(function () {});
          var claim = popup.querySelector('[data-scratch-step="claim"]');
          var success = popup.querySelector('[data-scratch-step="success"]');
          if (claim) claim.hidden = true;
          if (success) { success.hidden = false; success.classList.add('is-in'); }
        });
      });
    }

    if (signedUp()) return;
    if (until(KEY_DISMISSED)) { showChip(); return; }
    if (delay <= 0) open(); else setTimeout(open, delay * 1000);
  }

  return { init: init, open: function () { if (popup) open(); } };
}());

/* Sends a Shopify customer (newsletter) form in the background so the page
   never reloads. Resolves either way: if Shopify answers with its bot
   challenge the visitor still gets the success state. */
LUVZ.signup = function (form) {
  return fetch(form.action, {
    method: 'POST',
    body: new FormData(form),
    headers: { 'Accept': 'text/html' },
    credentials: 'same-origin'
  }).then(function (r) { return r; }).catch(function () { return null; });
};

/* Every other sign-up form on the page (the "First to the drop" card) gets
   the same no-reload treatment with an animated thank-you. */
document.addEventListener('submit', function (e) {
  var form = e.target;
  if (!form.matches || !form.matches('form[data-signup]')) return;
  e.preventDefault();
  var btn = form.querySelector('[type="submit"]');
  if (btn) btn.disabled = true;
  LUVZ.signup(form).then(function () {
    var msg = form.getAttribute('data-success') || 'You’re on the list.';
    var done = document.createElement('div');
    done.className = 'signup-done';
    done.setAttribute('role', 'status');
    done.innerHTML = '<span class="signup-done__tick" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><b></b>';
    done.querySelector('b').textContent = msg;
    form.classList.add('is-leaving');
    setTimeout(function () {
      form.replaceWith(done);
      requestAnimationFrame(function () { done.classList.add('is-in'); });
    }, 260);
  });
});
